import { useState, useCallback } from 'react';
import {
  CourierPackageApi,
  MailBankApi,
} from '@shared/sdk-v1/apis';
import { CourierPackage } from '@shared/sdk-v1/models';
import { apiConfig } from '../shared/api-config';
import { useSelector } from 'react-redux';
import { getCourier } from '@state/courierSlice';

interface UsePackageDetailReturn {
  package: CourierPackage | null;
  isLoading: boolean;
  error: Error | null;
  fetchPackage: () => Promise<void>;
  refresh: () => Promise<void>;
  sendStatusAlert: (notificationTypes: string[]) => Promise<boolean>;
  isEmailSending: boolean;
  emailError: Error | null;
  getPaymentLink: () => string | null;
  paymentGateway: 'stripe' | 'wipay' | 'fygaro' | null;
  hasPaymentGateway: boolean;
}

export const usePackageDetail = (id: string): UsePackageDetailReturn => {
  const [packageData, setPackageData] = useState<CourierPackage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [emailError, setEmailError] = useState<Error | null>(null);
  const courier = useSelector(getCourier);

  // Determine payment gateway from already-loaded courier data in Redux
  const hasStripe = Boolean(courier?.stripeCredentials?.isStripeApiEnabled);
  const hasWipay = Boolean(courier?.wipayCredentials?.isWipayApiEnabled);
  const hasFygaro = Boolean(courier?.courierSetting?.isFygaroApiEnabled);
  const hasPaymentGateway = hasStripe || hasWipay || hasFygaro;

  const paymentGateway: 'stripe' | 'wipay' | 'fygaro' | null = hasStripe
    ? 'stripe'
    : hasWipay
    ? 'wipay'
    : hasFygaro
    ? 'fygaro'
    : null;

  const fetchPackage = useCallback(async () => {
    if (!id) return;

    const packageApi = new CourierPackageApi(apiConfig());
    setIsLoading(true);
    setError(null);

    try {
      // Include invoice relation
      const filter = {
        include: ['invoice'],
      };

      const response = await packageApi.courierPackageFindById({
        id,
        filter: JSON.stringify(filter),
      });

      setPackageData(response);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to fetch package details')
      );
      console.error('Error fetching package details:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const getPaymentLink = useCallback((): string | null => {
    if (!packageData?.invoice?.id || !packageData.courierId) return null;

    const baseUrl = import.meta.env.VITE_API_BASE_URL;
    const courierId = packageData.courierId;
    const invoiceId = packageData.invoice.id;

    switch (paymentGateway) {
      case 'stripe':
        return `${baseUrl}/Stripes/make-payment?courierId=${courierId}&ids=${invoiceId}`;
      case 'wipay':
        return `${baseUrl}/Wipays/make-payment?courierId=${courierId}&ids=${invoiceId}`;
      case 'fygaro':
        return `${baseUrl}/Invoices/make-payment?courierId=${courierId}&ids=${invoiceId}`;
      default:
        return null;
    }
  }, [packageData?.invoice?.id, packageData?.courierId, paymentGateway]);

  const sendStatusAlert = useCallback(
    async (notificationTypes: string[] = ['email', 'whatsapp']) => {
      if (!packageData?.id) {
        setEmailError(new Error('Package not found'));
        return false;
      }

      setIsEmailSending(true);
      setEmailError(null);

      try {
        const mailBankApi = new MailBankApi(apiConfig());

        // Call the sendPackageStatusAlert endpoint
        await mailBankApi.mailBankSendPackageStatusAlert({
          mailBankSendPackageStatusAlertRequest: {
            packageId: packageData.id.toString(),
          },
        });

        return true;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to send status alert');
        setEmailError(error);
        console.error('Error sending status alert:', err);
        return false;
      } finally {
        setIsEmailSending(false);
      }
    },
    [packageData?.id]
  );

  const refresh = useCallback(() => {
    return fetchPackage();
  }, [fetchPackage]);

  return {
    package: packageData,
    isLoading,
    error,
    fetchPackage,
    refresh,
    sendStatusAlert,
    isEmailSending,
    emailError,
    getPaymentLink,
    paymentGateway,
    hasPaymentGateway,
  };
};

export default usePackageDetail;
